// src/auth/AuthProvider.tsx
"use client";

import { PlayerItem, PlayerPokemonTeam, Pokemon } from "prisma-layer";
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RouteNames } from "@/types/route-names";
import { PokeTeam } from "@/types";
import { InventoryPokemonsOutput } from "types/web-app";

export enum Intent {
  SELECTING_POKEMONS_TO_BREED = "selecting_pokemons_to_breed",
  SELECTING_POKEMONS_FOR_TEAM = "selecting_pokemons_for_team",
  SELECTING_POKEMONS_TO_SEND = "selecting_pokemons_to_send",
  SELECTING_POKEMON_FOR_MAIN_TEAM = "selecting_pokemon_for_main_team",
  SELECTING_POKEMON_FOR_COLLECTION = "selecting_pokemon_for_collection",
}

interface SPAContextValue {
  selectedPokemons: InventoryPokemonsOutput["player_pokemons"];
  setSelectedPokemons: Dispatch<
    SetStateAction<InventoryPokemonsOutput["player_pokemons"]>
  >;
  selectedItems: PlayerItem[];
  setSelectedItems: Dispatch<SetStateAction<PlayerItem[]>>;
  selectedTeam: PokeTeam | undefined;
  setSelectedTeam: Dispatch<SetStateAction<PokeTeam | undefined>>;
  intent: Intent | undefined;
  setIntent: Dispatch<SetStateAction<Intent | undefined>>;
  currentRoute: RouteNames | undefined;
  setCurrentRoute: Dispatch<SetStateAction<RouteNames | undefined>>;
  previousRoute: RouteNames | undefined;
  setPreviousRoute: Dispatch<SetStateAction<RouteNames | undefined>>;
  viewedPlayerId: string | undefined;
  setViewedPlayerId: Dispatch<SetStateAction<string | undefined>>;
  registerBackAction: (action: () => void) => () => void;
  popBackAction: () => boolean;
  clearBackActions: () => void;
}

const SPAContext = createContext<SPAContextValue | null>(null);

export const SPAProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  type BackActionEntry = { id: number; action: () => void };
  const backActionId = useRef(0);

  const [selectedPokemons, setSelectedPokemons] = useState<
    InventoryPokemonsOutput["player_pokemons"]
  >([]);
  const [selectedItems, setSelectedItems] = useState<PlayerItem[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<PokeTeam | undefined>(
    undefined,
  );
  const [intent, setIntent] = useState<Intent | undefined>(undefined);
  const [currentRoute, setCurrentRoute] = useState<RouteNames | undefined>();
  const [previousRoute, setPreviousRoute] = useState<RouteNames | undefined>();
  const [viewedPlayerId, setViewedPlayerId] = useState<string | undefined>(
    undefined,
  );
  const [backActions, setBackActions] = useState<BackActionEntry[]>([]);

  const registerBackAction = useCallback((action: () => void) => {
    const id = backActionId.current++;
    setBackActions((prev) => [...prev, { id, action }]);

    return () => {
      setBackActions((prev) => prev.filter((entry) => entry.id !== id));
    };
  }, []);

  const popBackAction = useCallback(() => {
    let entry: BackActionEntry | undefined;
    setBackActions((prev) => {
      if (!prev.length) return prev;
      entry = prev[prev.length - 1];
      return prev.slice(0, -1);
    });

    if (entry) {
      entry.action();
      return true;
    }

    return false;
  }, []);

  const clearBackActions = useCallback(() => setBackActions([]), []);

  // Reset back stack when returning to home
  useEffect(() => {
    if (!currentRoute) {
      clearBackActions();
    }
  }, [clearBackActions, currentRoute]);

  const value = useMemo<SPAContextValue>(
    () => ({
      selectedPokemons,
      setSelectedPokemons,
      selectedItems,
      setSelectedItems,
      selectedTeam,
      setSelectedTeam,
      intent,
      setIntent,
      currentRoute,
      setCurrentRoute,
      previousRoute,
      setPreviousRoute,
      viewedPlayerId,
      setViewedPlayerId,
      registerBackAction,
      popBackAction,
      clearBackActions,
    }),
    [
      selectedPokemons,
      setSelectedPokemons,
      selectedItems,
      setSelectedItems,
      selectedTeam,
      setSelectedTeam,
      intent,
      setIntent,
      currentRoute,
      setCurrentRoute,
      previousRoute,
      setPreviousRoute,
      viewedPlayerId,
      setViewedPlayerId,
      registerBackAction,
      popBackAction,
      clearBackActions,
    ],
  );

  return <SPAContext.Provider value={value}>{children}</SPAContext.Provider>;
};

export function useSPAContext() {
  const ctx = useContext(SPAContext);
  if (!ctx) throw new Error("useSPAContext must be used within <SPAProvider>");

  return ctx;
}
